"use client";

import { useLayoutEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import Image from "@/components/shared/image";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getActiveLenis } from "@/components/providers/lenis-instance";
import { useAuth } from "@/components/auth/AuthProvider";
import { useAnalytics } from "@/lib/analytics";
import { useColorTheme } from "@/components/providers/color-theme-provider";
import { cn } from "@/lib/utils";
import {
  getHomepageMotionEnvironment,
  shouldUseHeavyHomepageMotion,
} from "@/components/LandingPage/mobile-motion-policy";

const TRUSTED_AVATARS = [
  "/creators/creator-3.webp",
  "/creators/creator-7.webp",
  "/sneha_28_72w.webp",
] as const;

// Floating icon bubbles — each with its own color palette & position
const FLOATING_ICONS = [
  {
    // Heart / Engagement — pink
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
    color: "#f472b6",
    bg: "rgba(244,114,182,0.13)",
    border: "rgba(244,114,182,0.30)",
    shadow: "0 8px 28px rgba(244,114,182,0.32), 0 2px 8px rgba(244,114,182,0.18)",
    pos: "left-[10%] top-[11%]",
    tilt: -9,
  },
  {
    // Chat / DM — cyan
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <circle cx="9" cy="10" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="12" cy="10" r="0.8" fill="currentColor" stroke="none" />
        <circle cx="15" cy="10" r="0.8" fill="currentColor" stroke="none" />
      </svg>
    ),
    color: "#22d3ee",
    bg: "rgba(34,211,238,0.11)",
    border: "rgba(34,211,238,0.28)",
    shadow: "0 8px 28px rgba(34,211,238,0.28), 0 2px 8px rgba(34,211,238,0.16)",
    pos: "left-[6.5%] top-[41%]",
    tilt: -5,
  },
  {
    // Analytics / Growth — emerald
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
    color: "#34d399",
    bg: "rgba(52,211,153,0.11)",
    border: "rgba(52,211,153,0.28)",
    shadow: "0 8px 28px rgba(52,211,153,0.28), 0 2px 8px rgba(52,211,153,0.16)",
    pos: "left-[13.5%] bottom-[11%]",
    tilt: -11,
  },
  {
    // Team / Creators — violet
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    color: "#a78bfa",
    bg: "rgba(167,139,250,0.11)",
    border: "rgba(167,139,250,0.28)",
    shadow: "0 8px 28px rgba(167,139,250,0.32), 0 2px 8px rgba(167,139,250,0.18)",
    pos: "right-[9%] top-[10.5%]",
    tilt: 9,
  },
  {
    // Monetise / Dollar — amber
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    color: "#fbbf24",
    bg: "rgba(251,191,36,0.11)",
    border: "rgba(251,191,36,0.28)",
    shadow: "0 8px 28px rgba(251,191,36,0.28), 0 2px 8px rgba(251,191,36,0.16)",
    pos: "right-[5.5%] top-[39%]",
    tilt: 5,
  },
  {
    // Link / Automation — sky
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    color: "#60a5fa",
    bg: "rgba(96,165,250,0.11)",
    border: "rgba(96,165,250,0.28)",
    shadow: "0 8px 28px rgba(96,165,250,0.28), 0 2px 8px rgba(96,165,250,0.16)",
    pos: "right-[14.5%] bottom-[11.5%]",
    tilt: 12,
  },
] as const;

export default function CTA() {
  const { openAuth } = useAuth();
  const { track } = useAnalytics();
  const { themeColor } = useColorTheme();
  const sectionRef = useRef<HTMLElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const outerTailRef = useRef<HTMLDivElement | null>(null);
  const middleTailRef = useRef<HTMLDivElement | null>(null);
  const outerDotRef = useRef<HTMLDivElement | null>(null);
  const middleDotRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  useLayoutEffect(() => {
    if (!shouldUseHeavyHomepageMotion(getHomepageMotionEnvironment())) return;

    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      getActiveLenis()?.resize();

      if (shellRef.current) {
        gsap.set(shellRef.current, { maxWidth: "56rem" });
      }

      gsap.to(shellRef.current, {
        maxWidth: "80rem",
        duration: 1.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 82%",
          once: true,
        },
      });

      gsap.to(outerTailRef.current, {
        rotation: 360,
        duration: 34,
        ease: "none",
        repeat: -1,
        transformOrigin: "50% 50%",
      });

      gsap.to(middleTailRef.current, {
        rotation: -360,
        duration: 24,
        ease: "none",
        repeat: -1,
        transformOrigin: "50% 50%",
      });

      gsap.to(outerDotRef.current, {
        opacity: 0.45,
        scale: 0.82,
        duration: 0.85,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(middleDotRef.current, {
        opacity: 0.5,
        scale: 0.84,
        duration: 0.72,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      cardRefs.current.forEach((card, idx) => {
        if (!card) return;
        const tilt = Number(card.dataset.tilt ?? 0);
        gsap.set(card, { rotation: tilt, force3D: true });
        gsap.to(card, {
          y: idx % 2 === 0 ? -14 : -10,
          rotation: tilt + (idx % 2 === 0 ? 1.6 : -1.4),
          duration: 4.1 + idx * 0.22,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      });
      ScrollTrigger.refresh();
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="cta" ref={sectionRef} className="container-padding bg-white/80 backdrop-blur-sm pt-10 pb-1 dark:bg-black/80 sm:pt-16 sm:pb-6">
      <div ref={shellRef} className="mx-auto w-full max-w-7xl">
        <div className="relative overflow-hidden rounded-[2rem] border border-zinc-800/50 bg-gradient-to-br from-[#050816] via-[#050113] to-[#090016] px-4 py-12 text-center text-white sm:rounded-[2.5rem] sm:px-10 sm:py-20 md:px-14">
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative h-[min(105vw,560px)] w-[min(105vw,560px)] rounded-full border border-blue-300/12 sm:h-[min(110vw,680px)] sm:w-[min(110vw,680px)]">
              <div className="absolute inset-[12.5%] rounded-full border border-fuchsia-300/14" />
              <div className="absolute inset-[25%] rounded-full border border-cyan-300/20" />

              <div ref={outerTailRef} className="absolute inset-0 [animation:spin-slow_34s_linear_infinite]">
                <div className="absolute inset-0 rounded-full [mask:radial-gradient(farthest-side,transparent_calc(100%-1.5px),#000_calc(100%-1.5px))] [background:conic-gradient(from_-82deg,transparent_0deg,transparent_14deg,rgba(103,232,249,0.12)_20deg,rgba(103,232,249,0.48)_27deg,rgba(125,211,252,0.96)_35deg,transparent_44deg,transparent_360deg)]" />
                <div className="absolute left-1/2 top-0 h-8 w-8 -translate-x-[34%] -translate-y-1/2">
                  <div
                    ref={outerDotRef}
                    className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-gradient-to-br from-white via-sky-200 to-cyan-400 shadow-[0_0_22px_rgba(56,189,248,0.95)] animate-pulse"
                  />
                </div>
              </div>

              <div ref={middleTailRef} className="absolute inset-[12.5%] rounded-full [animation:spin-slow_24s_linear_infinite_reverse]">
                <div className="absolute inset-0 rounded-full [mask:radial-gradient(farthest-side,transparent_calc(100%-1.2px),#000_calc(100%-1.2px))] [background:conic-gradient(from_-98deg,transparent_0deg,transparent_315deg,rgba(244,114,182,0.95)_324deg,rgba(236,72,153,0.50)_333deg,rgba(244,114,182,0.14)_340deg,transparent_348deg,transparent_360deg)]" />
                <div className="absolute left-1/2 top-0 h-7 w-7 -translate-x-[-6%] -translate-y-1/2">
                  <div
                    ref={middleDotRef}
                    className="absolute left-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-gradient-to-br from-white via-fuchsia-200 to-purple-400 shadow-[0_0_18px_rgba(232,121,249,0.95)] animate-pulse"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute -left-36 -top-32 hidden h-72 w-72 rounded-full bg-purple-500/25 blur-3xl sm:block" />
          <div className="pointer-events-none absolute -right-28 bottom-[-60px] hidden h-64 w-64 rounded-full bg-sky-500/20 blur-3xl sm:block" />

          {FLOATING_ICONS.map((item, i) => (
            <div
              key={i}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              data-tilt={item.tilt}
              aria-hidden
              className={`pointer-events-none absolute hidden md:flex md:items-center md:justify-center ${i % 2 === 0 ? "animate-float-soft" : "animate-float-soft-delay"} ${item.pos}`}
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                background: item.bg,
                border: `1.5px solid ${item.border}`,
                boxShadow: item.shadow,
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                color: item.color,
              }}
            >
              {item.icon}
            </div>
          ))}

          <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center">
            <div className="inline-flex items-center gap-2 text-white/90">
              <div className="flex -space-x-1.5">
                {TRUSTED_AVATARS.map((src) => (
                  <span key={src} className="inline-flex h-6 w-6 items-center justify-center overflow-hidden rounded-full border border-white/20">
                    <Image src={src} alt="" width={48} height={48} className="h-full w-full object-cover" />
                  </span>
                ))}
              </div>
              <div className="flex flex-col items-start leading-none">
                <span className="text-[12px]">⭐⭐⭐⭐⭐</span>
                <span className="mt-1 text-[10px] font-semibold tracking-wide sm:text-[11px]">Built for creators, brands, and agencies</span>
              </div>
            </div>

            <h3 className="mt-4 text-balance text-[1.85rem] font-bold tracking-tight leading-[1.12] sm:mt-5 sm:text-4xl md:text-[2.8rem] md:leading-[1.05]">
              Reply <span className="bg-primary-gradient bg-clip-text text-transparent">instantly.</span>
              <br />
              Convert more followers automatically.
            </h3>

            <div className="mt-6 flex w-full flex-wrap items-center justify-center gap-3 sm:mt-8">
              <Button
                size="lg"
                onClick={() => {
                  track("cta_clicked", {
                    cta_id: "CTA_Final",
                    button_text: "Start Free",
                  });
                  openAuth();
                }}
                className={cn(
                  "h-11 w-full max-w-xs rounded-full bg-primary-gradient px-8 text-sm font-bold text-white dark:text-black sm:h-12 sm:w-auto sm:max-w-none sm:px-10",
                  "w-fit mx-auto sm:mx-0",
                  themeColor === "yellow" && "!text-black"
                )}
              >
                Start Free
              </Button>
            </div>
            <p className="mt-2.5 text-[11px] font-medium text-white/75 sm:mt-3 sm:text-xs">No credit card. Disconnect anytime</p>
          </div>
        </div>
      </div>
    </section>
  );
}
